import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Eye, Clock, Play, ExternalLink } from 'lucide-react';
import BannerAdDisplay from './BannerAdDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [vastAds, setVastAds] = useState({ preroll: [], midroll: [], postroll: [] });
  const [currentAd, setCurrentAd] = useState(null);
  const [adPosition, setAdPosition] = useState('pre-roll');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [impressionTracked, setImpressionTracked] = useState(false);

  useEffect(() => {
    if (id) {
      fetchVideo();
      fetchVastAds();
    }
  }, [id]);

  useEffect(() => {
    if (video) {
      fetchRelatedVideos();
    }
  }, [video]);

  const fetchVideo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/videos/${id}`);
      setVideo(response.data);
    } catch (error) {
      console.error('Error fetching video:', error);
      setError('Video not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedVideos = async () => {
    try {
      const params = new URLSearchParams();
      if (video.category) {
        params.append('category', video.category);
      }
      params.append('limit', '6');
      
      const response = await axios.get(`${API}/videos?${params}`);
      // Filter out current video
      const filtered = response.data.filter(v => v.id !== video.id);
      setRelatedVideos(filtered.slice(0, 5));
    } catch (error) {
      console.error('Error fetching related videos:', error);
    }
  };

  const fetchVastAds = async () => {
    try {
      const [prerollRes, midrollRes, postrollRes] = await Promise.all([
        axios.get(`${API}/vast-ads/pre-roll/parsed`),
        axios.get(`${API}/vast-ads/mid-roll/parsed`), 
        axios.get(`${API}/vast-ads/post-roll/parsed`)
      ]);
      
      setVastAds({
        preroll: prerollRes.data,
        midroll: midrollRes.data,
        postroll: postrollRes.data
      });
      
      // Show pre-roll ad if available
      if (prerollRes.data.length > 0) {
        setCurrentAd(prerollRes.data[0]);
        setAdPosition('pre-roll');
        setImpressionTracked(false);
      }
    } catch (error) {
      console.error('Error fetching VAST ads:', error);
    }
  };

  const handleSkipAd = () => {
    setCurrentAd(null);
    setAdPosition(null);
    setImpressionTracked(false);
  };

  const handleVideoEnd = () => {
    // Show post-roll ad if available
    if (vastAds.postroll.length > 0) {
      setCurrentAd(vastAds.postroll[0]);
      setAdPosition('post-roll');
      setImpressionTracked(false);
    }
  };

  const trackImpression = async (ad) => {
    if (!ad.impression_tracking || ad.impression_tracking.length === 0 || impressionTracked) {
      return;
    }

    console.log('Tracking impression for URLs:', ad.impression_tracking);
    
    // Send tracking requests directly to VAST URLs using multiple methods
    ad.impression_tracking.forEach(async (url) => {
      try {
        // Method 1: Fetch (preferred)
        fetch(url, { 
          method: 'GET', 
          mode: 'no-cors',
          cache: 'no-cache'
        }).catch(() => {});

        // Method 2: Image pixel (fallback)
        const img = new Image();
        img.src = url;

        // Method 3: Backend proxy (for logging)
        axios.post(`${API}/vast/track/impression`, { urls: [url] }).catch(() => {});
        
        console.log('✅ Impression ping sent to:', url);
      } catch (error) {
        console.warn('Impression tracking failed for URL:', url, error);
      }
    });

    setImpressionTracked(true);
  };

  const trackClick = async (ad) => {
    if (!ad.click_tracking || ad.click_tracking.length === 0) {
      return;
    }

    console.log('Tracking click for URLs:', ad.click_tracking);

    // Send click tracking requests directly to VAST URLs using multiple methods
    ad.click_tracking.forEach(async (url) => {
      try {
        // Method 1: Fetch (preferred)
        fetch(url, { 
          method: 'GET', 
          mode: 'no-cors',
          cache: 'no-cache'
        }).catch(() => {});

        // Method 2: Image pixel (fallback)
        const img = new Image();
        img.src = url;

        // Method 3: Backend proxy (for logging)
        axios.post(`${API}/vast/track/click`, { urls: [url] }).catch(() => {});
        
        console.log('✅ Click ping sent to:', url);
      } catch (error) {
        console.warn('Click tracking failed for URL:', url, error);
      }
    });
  };

  const handleAdClick = async () => {
    if (!currentAd) return;

    // Track the click
    await trackClick(currentAd);

    // Open click-through URL in new tab
    if (currentAd.click_through) {
      window.open(currentAd.click_through, '_blank', 'noopener,noreferrer');
    }
  };

  const renderVideoEmbed = (videoUrl) => {
    return (
      <div className="video-container relative">
        {/* Always show main video iframe */}
        <iframe
          src={videoUrl}
          title={video?.title}
          allowFullScreen
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
        
        {/* VAST Ad Overlay - shows over the video when there's an ad */}
        {currentAd && (
          <div className="absolute inset-0 bg-black/90 flex items-center justify-center">
            {/* VAST Ad Video Player */}
            <div className="relative w-full h-full max-w-4xl">
              <video
                className="w-full h-full object-contain cursor-pointer"
                autoPlay
                muted
                playsInline
                onEnded={handleSkipAd}
                onLoadedData={() => trackImpression(currentAd)}
                onPlay={() => trackImpression(currentAd)}
                onClick={handleAdClick}
                data-testid="vast-ad-video"
                key={currentAd.id || 'demo-ad'}
              >
                {/* Use actual VAST video URL or fallback */}
                <source 
                  src={currentAd.media_url || "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"} 
                  type="video/mp4" 
                />
                <source 
                  src="https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
              
              {/* Clickable overlay for better click detection */}
              {currentAd.click_through && (
                <div 
                  className="absolute inset-0 cursor-pointer z-10"
                  onClick={handleAdClick}
                  data-testid="ad-click-overlay"
                >
                  <div className="absolute top-4 left-4 bg-blue-600/80 text-white px-2 py-1 rounded text-xs">
                    Click for more info
                  </div>
                </div>
              )}
              
              {/* Ad overlay info */}
              <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded text-sm z-20">
                Ad by {currentAd.vast_ad_name || currentAd.name || 'VAST Network'}
              </div>
              
              {/* Always visible skip button */}
              <button
                onClick={handleSkipAd}
                className="absolute bottom-4 right-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold z-50"
                data-testid="skip-ad-btn"
              >
                Skip Ad →
              </button>
              
              {/* Duration indicator */}
              {currentAd.duration && (
                <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm z-20">
                  {currentAd.duration}
                </div>
              )}
              
              {/* Ad status */}
              <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm z-20">
                {impressionTracked ? '✓ Impression Tracked' : 'VAST Advertisement'}
              </div>
              
              {/* Click tracking indicator */}
              {currentAd.click_tracking && currentAd.click_tracking.length > 0 && (
                <div className="absolute bottom-16 left-4 bg-green-600/70 text-white px-2 py-1 rounded text-xs z-20">
                  Click tracking enabled
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="loading-spinner"></div>
          <span className="text-white">Loading video...</span>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-white mb-4">Video Not Found</h2>
          <p className="text-white/70 mb-6">The video you're looking for doesn't exist or has been removed.</p>
          <Button
            onClick={() => navigate('/')}
            className="btn-hover bg-gradient-to-r from-purple-600 to-pink-600 text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="glass-dark border-b border-white/10 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 text-white hover:text-purple-300">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back to Videos</span>
          </Link>
          
          <h1 className="text-xl font-brand font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
            usexxx
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Video Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player */}
            <div className="aspect-video bg-black rounded-lg overflow-hidden" data-testid="video-player">
              {renderVideoEmbed(video.url)}
            </div>
            
            {/* Video Info */}
            <Card className="glass-dark border-white/20">
              <CardContent className="p-6">
                <h1 className="text-2xl font-bold text-white mb-4">{video.title}</h1>
                
                <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-white/70">
                  <span className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    {video.views.toLocaleString()} views
                  </span>
                  {video.duration && (
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {video.duration}
                    </span>
                  )}
                  {video.category && (
                    <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                      {video.category}
                    </Badge>
                  )}
                </div>
                
                {video.description && (
                  <div className="mb-4">
                    <p className="text-white/80 leading-relaxed">{video.description}</p>
                  </div>
                )}
                
                {/* Tags */}
                {video.tags && video.tags.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-white font-medium mb-2">Tags:</h3>
                    <div className="flex flex-wrap gap-2">
                      {video.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="border-white/30 text-white/70">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Source Link */}
                <div className="pt-4 border-t border-white/10">
                  <a 
                    href={video.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center text-purple-400 hover:text-purple-300 text-sm"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    View Original Source
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Sidebar - Related Videos & Banner Ads */}
          <div className="space-y-6">
            {/* Banner Ad */}
            <BannerAdDisplay position="video-page" />
            
            {relatedVideos.length > 0 && (
              <Card className="glass-dark border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Related Videos</h3>
                  <div className="space-y-4">
                    {relatedVideos.map((relatedVideo) => (
                      <Link
                        key={relatedVideo.id}
                        to={`/video/${relatedVideo.id}`}
                        className="block card-hover p-3 rounded-lg glass border border-white/10"
                      >
                        <div className="flex space-x-3">
                          <div className="w-24 h-16 bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded flex-shrink-0 flex items-center justify-center">
                            {relatedVideo.thumbnail ? (
                              <img 
                                src={relatedVideo.thumbnail} 
                                alt={relatedVideo.title}
                                className="w-full h-full object-cover rounded"
                              />
                            ) : (
                              <Play className="h-6 w-6 text-white/50" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white text-sm font-medium line-clamp-2 mb-1">
                              {relatedVideo.title}
                            </h4>
                            <div className="flex items-center space-x-2 text-xs text-white/60">
                              <span className="flex items-center">
                                <Eye className="h-3 w-3 mr-1" />
                                {relatedVideo.views.toLocaleString()}
                              </span>
                              {relatedVideo.duration && (
                                <span className="flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {relatedVideo.duration}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPage;